import React, { useEffect, useMemo, useState } from 'react';
import './hoja.css';
import logo from '../../../images/ensenartecorto.png';
import baseData from './data.json';
import GenerarPdfButton from './GenerarPdfButton';
import { obtenerTodosProgramas } from '../../../../funtions';

function Hoja({ isOpen, estudiante, onClose }) {
  const [programas, setProgramas] = useState([]);
  const [selectedProgramCodigo, setSelectedProgramCodigo] = useState('');
  const [selectedCostIndexes, setSelectedCostIndexes] = useState([]);
  const [descuentoPercent, setDescuentoPercent] = useState(0);
  const [fechaLimite, setFechaLimite] = useState('');
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      try {
        const data = await obtenerTodosProgramas();
        setProgramas(data || []);
        if (data && data.length > 0) setSelectedProgramCodigo(data[0].codigo);
      } catch (e) {
        console.error(e);
        setError('No se pudieron cargar los programas.');
      }
    };
    load();

    // Fecha límite por defecto: 30 días a partir de hoy, en formato yyyy-mm-dd para el <input type="date">
    const defaultDue = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    setFechaLimite(defaultDue.toISOString().slice(0, 10));
  }, [isOpen]);

  // Al cambiar de programa, reinicia la selección de costos
  useEffect(() => {
    setSelectedCostIndexes([]);
  }, [selectedProgramCodigo]);

  const selectedProgram = programas.find((p) => p.codigo === selectedProgramCodigo) || programas[0] || null;
  const costes = selectedProgram?.costes || [];

  const fullName = estudiante
    ? [
        estudiante.primerNombre,
        estudiante.segundoNombre,
        estudiante.primerApellido,
        estudiante.segundoApellido,
      ]
        .filter(Boolean)
        .join(' ')
    : '';

  const formatCurrency = (value) => {
    if (value === undefined || value === null || value === '') return '';
    const number = typeof value === 'number' ? value : Number(String(value).replace(/[^0-9.-]/g, ''));
    if (Number.isNaN(number)) return String(value);
    return number.toLocaleString('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    });
  };

  // Convierte cualquier valor (número, string con formato, etc.) a número puro.
  // La plantilla (template.html) es la que aplica el formato de moneda con su
  // propio filtro fmt(), así que aquí SIEMPRE se le debe pasar números crudos.
  const toNumber = (value) => {
    if (typeof value === 'number') return value;
    const n = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isNaN(n) ? 0 : n;
  };

  const formatDate = (isoDateOrDate) => {
    const d = isoDateOrDate instanceof Date ? isoDateOrDate : new Date(`${isoDateOrDate}T00:00:00`);
    return d.toLocaleDateString('es-CO');
  };

  const toggleCoste = (idx) => {
    setSelectedCostIndexes((prev) => (prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]));
  };

  // ---- Cálculos en vivo del resumen (subtotal, descuento, total) ----
  const { subtotal, descuentoValor, total } = useMemo(() => {
    const sub = costes
      .filter((_, idx) => selectedCostIndexes.includes(idx))
      .reduce((acc, c) => acc + toNumber(c.valor), 0);
    const pct = Number(descuentoPercent) || 0;
    const desc = Math.round((sub * pct) / 100);
    return { subtotal: sub, descuentoValor: desc, total: sub - desc };
  }, [costes, selectedCostIndexes, descuentoPercent]);

  if (!isOpen || !estudiante) return null;

  const buildOrderData = () => {
    const now = new Date();
    const orderNumber = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(now.getTime()).slice(-5)}`;
    const fechaImpresion = now.toLocaleDateString('es-CO');
    const fechaLimiteTexto = formatDate(fechaLimite);

    const selectedCostes = costes.filter((_, idx) => selectedCostIndexes.includes(idx));

    return {
      institucion: baseData.institucion,
      orden: {
        numero: orderNumber,
        fecha_impresion: fechaImpresion,
      },
      estudiante: {
        nombre: fullName,
        identificacion: estudiante.documento || estudiante.identificacion || '',
        programa: selectedProgram?.nombre || '',
        periodo: estudiante.periodo || '',
      },
      pagos: selectedCostes.map((c) => ({
        concepto: c.definicion || 'Costo del programa',
        fecha: fechaImpresion,
        valor: toNumber(c.valor),
      })),
      cuentas: baseData.cuentas.map((cuenta) => ({
        etiqueta: cuenta.etiqueta,
        valor: toNumber(cuenta.valor),
      })),
      datos_bancarios: baseData.datos_bancarios,
      descuento: {
        porcentaje: descuentoPercent > 0 ? `${descuentoPercent}% DCTO` : 'SIN DESCUENTO',
        concepto: descuentoPercent > 0 ? 'Descuento aplicado sobre el subtotal' : '—',
        valor: descuentoValor,
      },
      total_a_pagar: total,
      fecha_limite: fechaLimiteTexto,
    };
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Generar Orden de Pago</h3>
          <button className="btn-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="form-row">
            <div>
              <strong>Estudiante:</strong> {fullName}
            </div>
            <div>
              <strong>Identificación:</strong> {estudiante.documento || estudiante.identificacion || ''}
            </div>
          </div>

          <div className="form-field">
            <label>Programa / Curso</label>
            <select
              value={selectedProgramCodigo}
              onChange={(e) => setSelectedProgramCodigo(e.target.value)}
            >
              {programas.map((p) => (
                <option key={p.codigo} value={p.codigo}>
                  {p.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label>Pagos / costos a incluir</label>
            <div className="costes-list">
              {costes.length === 0 && <div className="empty-costes">No hay costos definidos para este programa.</div>}
              {costes.map((c, idx) => (
                <label key={idx} className="coste-option">
                  <input
                    type="checkbox"
                    checked={selectedCostIndexes.includes(idx)}
                    onChange={() => toggleCoste(idx)}
                  />
                  <span>
                    {c.definicion} — {formatCurrency(c.valor)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-row form-row-2">
            <div className="form-field">
              <label>Descuento (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={descuentoPercent}
                onChange={(e) => setDescuentoPercent(Number(e.target.value))}
              />
            </div>
            <div className="form-field">
              <label>Fecha límite de pago</label>
              <input type="date" value={fechaLimite} onChange={(e) => setFechaLimite(e.target.value)} />
            </div>
          </div>

          <div className="resumen-modal">
            <div className="resumen-linea">
              <span>Subtotal</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            <div className="resumen-linea">
              <span>Descuento {descuentoPercent > 0 ? `(${descuentoPercent}%)` : ''}</span>
              <span>- {formatCurrency(descuentoValor)}</span>
            </div>
            <div className="resumen-linea resumen-total">
              <span>Total a pagar</span>
              <span>{formatCurrency(total)}</span>
            </div>
          </div>

          {error && <div className="form-error">{error}</div>}
        </div>

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <GenerarPdfButton orderData={buildOrderData()} logo={logo} onStart={() => setGenerando(true)} onDone={() => setGenerando(false)} disabled={generando} archivoNombre={`orden_de_pago_${fullName.replace(/\s+/g, '_')}`} />
        </div>
      </div>
    </div>
  );
}

export default Hoja;